import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { habitaciones } from '../data/habitaciones';
import './AdminFinanzas.css';
import FloatingMenu from './FloatingMenu';

const AdminFinanzas = () => {
  const [ingresosMensuales, setIngresosMensuales] = useState({});
  const [ingresosAnuales, setIngresosAnuales] = useState({});
  const [ingresosPorHabitacion, setIngresosPorHabitacion] = useState({});
  const [mesActual, setMesActual] = useState(new Date().getMonth());
  const [anioActual, setAnioActual] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ingresoTotalAnual, setIngresoTotalAnual] = useState(0);
  const [ingresoTotalMensual, setIngresoTotalMensual] = useState(0);
  const [reservasMes, setReservasMes] = useState([]);
  const [todasReservas, setTodasReservas] = useState([]);
  const [ingresosPorTipoPago, setIngresosPorTipoPago] = useState({
    Efectivo: 0,
    Transferencia: 0,
    Tarjeta: 0
  });
  
  // Estado para controlar tarjetas colapsadas
  const [tarjetasColapsadas, setTarjetasColapsadas] = useState({
    ingresosMensuales: true,
    reservasMensuales: true,
    ingresosAnuales: true,
    ingresosHabitaciones: true,
    ingresosTipoPago: true
  });

  // Función para alternar el estado de colapso de una tarjeta
  const toggleTarjeta = (tarjeta) => {
    setTarjetasColapsadas(prev => ({
      ...prev,
      [tarjeta]: !prev[tarjeta]
    }));
  };

  // Array de nombres de meses
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Cargar datos de reservas al inicio
  useEffect(() => {
    cargarDatosFinancieros();
  }, []);

  // Actualizar reservas del mes cuando cambia el mes o año actual
  useEffect(() => {
    if (todasReservas.length > 0) {
      filtrarReservasPorMes();
      procesarIngresosPorTipoPago(todasReservas);
    }
  }, [mesActual, anioActual, todasReservas]);

  // Método para cargar todos los datos financieros
  const cargarDatosFinancieros = async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener todas las reservas
      const reservasCollection = collection(db, 'reservas');
      const reservasSnapshot = await getDocs(reservasCollection);
      const todasLasReservas = reservasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filtrar para excluir las reservas bloqueadas
      const reservas = todasLasReservas.filter(reserva => {
        // Excluir si el status es 'blocked' o el nombre es 'Bloqueado'
        const status = reserva.status;
        const nombre = reserva.nombre || reserva.name || '';
        return status !== 'blocked' && nombre !== 'Bloqueado';
      });

      // Guardar todas las reservas para usar en la visualización por mes
      setTodasReservas(reservas);

      console.log(`Se encontraron ${todasLasReservas.length} reservas en total`);
      console.log(`Se excluyen ${todasLasReservas.length - reservas.length} reservas bloqueadas`);
      console.log(`Procesando ${reservas.length} reservas válidas para estadísticas`);

      // Procesar datos financieros
      procesarIngresosMensuales(reservas);
      procesarIngresosAnuales(reservas);
      procesarIngresosPorHabitacion(reservas);
      procesarIngresosPorTipoPago(reservas);
      filtrarReservasPorMes();

      setLoading(false);
    } catch (err) {
      console.error("Error al cargar datos financieros:", err);
      setError("Error al cargar datos financieros. Por favor, intente nuevamente.");
      setLoading(false);
    }
  };

  // Filtrar las reservas del mes y año actual
  const filtrarReservasPorMes = () => {
    if (!todasReservas.length) return;

    const reservasFiltradas = todasReservas.filter(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada);
      }
      
      // Filtrar por mes y año actual
      return fechaEntrada.getMonth() === mesActual && fechaEntrada.getFullYear() === anioActual;
    });
    
    // Ordenar por fecha de entrada (más reciente primero)
    reservasFiltradas.sort((a, b) => {
      let fechaA, fechaB;
      
      if (a.fechaEntrada instanceof Timestamp) {
        fechaA = a.fechaEntrada.toDate();
      } else if (typeof a.fechaEntrada === 'object' && a.fechaEntrada.seconds) {
        fechaA = new Date(a.fechaEntrada.seconds * 1000);
      } else if (a.fechaEntrada instanceof Date) {
        fechaA = a.fechaEntrada;
      } else {
        fechaA = new Date(a.fechaEntrada);
      }
      
      if (b.fechaEntrada instanceof Timestamp) {
        fechaB = b.fechaEntrada.toDate();
      } else if (typeof b.fechaEntrada === 'object' && b.fechaEntrada.seconds) {
        fechaB = new Date(b.fechaEntrada.seconds * 1000);
      } else if (b.fechaEntrada instanceof Date) {
        fechaB = b.fechaEntrada;
      } else {
        fechaB = new Date(b.fechaEntrada);
      }
      
      return fechaB - fechaA; // Orden descendente por fecha
    });
    
    setReservasMes(reservasFiltradas);
  };

  // Procesar ingresos mensuales del año actual
  const procesarIngresosMensuales = (reservas) => {
    // Inicializar objeto para almacenar ingresos por mes
    const ingresosPorMes = {};
    let totalAnual = 0;

    // Inicializar todos los meses con cero
    for (let i = 0; i < 12; i++) {
      ingresosPorMes[i] = 0;
    }

    // Calcular ingresos por mes
    reservas.forEach(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada);
      }

      // Si la fecha es del año actual, sumar al mes correspondiente
      if (fechaEntrada.getFullYear() === anioActual) {
        const mes = fechaEntrada.getMonth();
        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
        
        ingresosPorMes[mes] += parseInt(precioTotal);
        totalAnual += parseInt(precioTotal);
      }
    });

    setIngresosMensuales(ingresosPorMes);
    setIngresoTotalMensual(ingresosPorMes[mesActual] || 0);
    setIngresoTotalAnual(totalAnual);
  };

  // Procesar ingresos anuales (últimos 5 años)
  const procesarIngresosAnuales = (reservas) => {
    // Inicializar objeto para almacenar ingresos por año
    const ingresosPorAnio = {};
    const anioActual = new Date().getFullYear();
    
    // Inicializar los últimos 5 años con cero
    for (let i = 0; i < 5; i++) {
      ingresosPorAnio[anioActual - i] = 0;
    }

    // Calcular ingresos por año
    reservas.forEach(reserva => {
      // Obtener fecha de entrada
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada);
      }

      const anio = fechaEntrada.getFullYear();
      
      // Si el año está en nuestro rango de 5 años, sumarlo
      if (ingresosPorAnio[anio] !== undefined) {
        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
        ingresosPorAnio[anio] += parseInt(precioTotal);
      }
    });

    setIngresosAnuales(ingresosPorAnio);
  };

  // Procesar ingresos por habitación
  const procesarIngresosPorHabitacion = (reservas) => {
    // Inicializar objeto para almacenar ingresos por habitación
    const ingresosPorHab = {};
    
    // Inicializar todas las habitaciones con cero
    habitaciones.forEach(hab => {
      ingresosPorHab[hab.id] = {
        nombre: hab.nombre,
        ingresoTotal: 0,
        numeroReservas: 0,
        ingresoAnual: 0,
        ingresoMensual: 0
      };
    });

    // Calcular ingresos por habitación
    reservas.forEach(reserva => {
      const habitacionId = reserva.habitacionId || reserva.roomId;
      
      if (habitacionId && ingresosPorHab[habitacionId]) {
        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
        
        // Incrementar ingreso total
        ingresosPorHab[habitacionId].ingresoTotal += parseInt(precioTotal);
        ingresosPorHab[habitacionId].numeroReservas += 1;
        
        // Obtener fecha de entrada
        let fechaEntrada;
        if (reserva.fechaEntrada instanceof Timestamp) {
          fechaEntrada = reserva.fechaEntrada.toDate();
        } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
          fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
        } else if (reserva.fechaEntrada instanceof Date) {
          fechaEntrada = reserva.fechaEntrada;
        } else {
          fechaEntrada = new Date(reserva.fechaEntrada);
        }
        
        // Si es de este año, incrementar ingreso anual
        if (fechaEntrada.getFullYear() === anioActual) {
          ingresosPorHab[habitacionId].ingresoAnual += parseInt(precioTotal);
          
          // Si es de este mes, incrementar ingreso mensual
          if (fechaEntrada.getMonth() === mesActual) {
            ingresosPorHab[habitacionId].ingresoMensual += parseInt(precioTotal);
          }
        }
      }
    });

    setIngresosPorHabitacion(ingresosPorHab);
  };

  // Procesar ingresos por tipo de pago
  const procesarIngresosPorTipoPago = (reservas) => {
    // Inicializar objeto para almacenar ingresos por tipo de pago
    const ingresosPorTipo = {
      Efectivo: 0,
      Transferencia: 0,
      Tarjeta: 0
    };

    // Filtrar reservas del mes y año actual
    const reservasActuales = reservas.filter(reserva => {
      let fechaEntrada;
      if (reserva.fechaEntrada instanceof Timestamp) {
        fechaEntrada = reserva.fechaEntrada.toDate();
      } else if (typeof reserva.fechaEntrada === 'object' && reserva.fechaEntrada.seconds) {
        fechaEntrada = new Date(reserva.fechaEntrada.seconds * 1000);
      } else if (reserva.fechaEntrada instanceof Date) {
        fechaEntrada = reserva.fechaEntrada;
      } else {
        fechaEntrada = new Date(reserva.fechaEntrada);
      }
      
      return fechaEntrada.getMonth() === mesActual && fechaEntrada.getFullYear() === anioActual;
    });

    // Calcular ingresos por tipo de pago
    reservasActuales.forEach(reserva => {
      const tipoPago = reserva.tipoPago || reserva.metodoPago || 'Efectivo';
      const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
      
      // Asignar al tipo correspondiente
      if (tipoPago.toLowerCase().includes('efectivo')) {
        ingresosPorTipo.Efectivo += parseInt(precioTotal);
      } else if (tipoPago.toLowerCase().includes('transferencia')) {
        ingresosPorTipo.Transferencia += parseInt(precioTotal);
      } else if (tipoPago.toLowerCase().includes('tarjeta')) {
        ingresosPorTipo.Tarjeta += parseInt(precioTotal);
      } else {
        // Por defecto, asignar a efectivo
        ingresosPorTipo.Efectivo += parseInt(precioTotal);
      }
    });

    setIngresosPorTipoPago(ingresosPorTipo);
  };

  // Formatear moneda (CLP)
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(valor);
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    
    let fechaObj;
    if (fecha instanceof Timestamp) {
      fechaObj = fecha.toDate();
    } else if (typeof fecha === 'object' && fecha.seconds) {
      fechaObj = new Date(fecha.seconds * 1000);
    } else if (fecha instanceof Date) {
      fechaObj = fecha;
    } else {
      fechaObj = new Date(fecha);
    }
    
    return fechaObj.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtener el nombre de la habitación por ID
  const obtenerNombreHabitacion = (id) => {
    const habitacion = habitaciones.find(h => h.id.toString() === id.toString());
    return habitacion ? habitacion.nombre : `Habitación ${id}`;
  };

  // Cambiar al mes siguiente
  const cambiarMesSiguiente = () => {
    if (mesActual === 11) {
      setMesActual(0);
      setAnioActual(anioActual + 1);
    } else {
      setMesActual(mesActual + 1);
    }
  };

  // Cambiar al mes anterior
  const cambiarMesAnterior = () => {
    if (mesActual === 0) {
      setMesActual(11);
      setAnioActual(anioActual - 1);
    } else {
      setMesActual(mesActual - 1);
    }
  };

  return (
    <div className="admin-finanzas">
      <h1>Finanzas - Análisis de Ingresos</h1>

      {/* Mensaje de carga/error */}
      {loading && <div className="message loading">Cargando datos financieros...</div>}
      {error && <div className="message error">{error}</div>}

      {!loading && !error && (
        <>
          {/* Resumen de ingresos */}
          <div className="resumen-ingresos">
            <div className="card ingreso-anual">
              <div className="card-header">
                <h2>Ingreso Anual {anioActual}</h2>
              </div>
              <div className="card-content">
                <div className="valor">{formatearMoneda(ingresoTotalAnual)}</div>
              </div>
            </div>
            
            <div className="card ingreso-mensual">
              <div className="mes-navegacion">
                <button onClick={cambiarMesAnterior}>&laquo;</button>
                <h2>{meses[mesActual]} {anioActual}</h2>
                <button onClick={cambiarMesSiguiente}>&raquo;</button>
              </div>
              <div className="valor">{formatearMoneda(ingresosMensuales[mesActual] || 0)}</div>
            </div>
          </div>

          {/* Ingresos por Tipo de Pago (movido a esta posición) */}
          <div className={`card ingresos-tipo-pago ${tarjetasColapsadas.ingresosTipoPago ? 'colapsado' : ''}`}>
            <div className="card-header">
              <h2>Ingresos por Tipo de Pago - {meses[mesActual]} {anioActual}</h2>
              <button 
                className="toggle-btn" 
                onClick={() => toggleTarjeta('ingresosTipoPago')}
                aria-label={tarjetasColapsadas.ingresosTipoPago ? 'Expandir' : 'Colapsar'}
              >
                {tarjetasColapsadas.ingresosTipoPago ? '+' : '−'}
              </button>
            </div>
            <div className="card-content">
              <div className="tabla-container">
                <table>
                  <thead>
                    <tr>
                      <th>Tipo de Pago</th>
                      <th>Ingreso</th>
                      <th>Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(ingresosPorTipoPago).map(([tipo, ingreso]) => {
                      const total = ingresosMensuales[mesActual] || 0;
                      const porcentaje = total > 0 ? ((ingreso / total) * 100).toFixed(1) : 0;
                      
                      return (
                        <tr key={tipo}>
                          <td>{tipo}</td>
                          <td>{formatearMoneda(ingreso)}</td>
                          <td>{porcentaje}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Total</th>
                      <th>{formatearMoneda(ingresosMensuales[mesActual] || 0)}</th>
                      <th>100%</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Ingresos por mes del año actual */}
          <div className={`card ingresos-mensuales ${tarjetasColapsadas.ingresosMensuales ? 'colapsado' : ''}`}>
            <div className="card-header">
              <h2>Ingresos Mensuales {anioActual}</h2>
              <button 
                className="toggle-btn" 
                onClick={() => toggleTarjeta('ingresosMensuales')}
                aria-label={tarjetasColapsadas.ingresosMensuales ? 'Expandir' : 'Colapsar'}
              >
                {tarjetasColapsadas.ingresosMensuales ? '+' : '−'}
              </button>
            </div>
            <div className="card-content">
              <div className="tabla-container">
                <table>
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th>Ingreso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(ingresosMensuales).map(mes => (
                      <tr key={mes} className={parseInt(mes) === mesActual ? 'mes-actual' : ''}>
                        <td>{meses[mes]}</td>
                        <td>{formatearMoneda(ingresosMensuales[mes])}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Total Anual</th>
                      <th>{formatearMoneda(ingresoTotalAnual)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Listado de reservas del mes */}
          <div className={`card reservas-mensuales ${tarjetasColapsadas.reservasMensuales ? 'colapsado' : ''}`}>
            <div className="card-header">
              <h2>Reservas de {meses[mesActual]} {anioActual}</h2>
              <button 
                className="toggle-btn" 
                onClick={() => toggleTarjeta('reservasMensuales')}
                aria-label={tarjetasColapsadas.reservasMensuales ? 'Expandir' : 'Colapsar'}
              >
                {tarjetasColapsadas.reservasMensuales ? '+' : '−'}
              </button>
            </div>
            <div className="card-content">
              <div className="tabla-container">
                {reservasMes.length > 0 ? (
                  <table>
                    <thead>
                      <tr>
                        <th>Fecha Entrada</th>
                        <th>Fecha Salida</th>
                        <th>Cliente</th>
                        <th>Habitación</th>
                        <th>Huéspedes</th>
                        <th>Duración</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservasMes.map(reserva => {
                        const habitacionId = reserva.habitacionId || reserva.roomId;
                        const precioTotal = reserva.precioTotal || reserva.totalPrice || 0;
                        const personasEnHabitacion = reserva.personasEnHabitacion || reserva.guests || 1;
                        const duracion = reserva.duracionEstancia || reserva.duration || 1;
                        const nombre = reserva.nombre || reserva.name || 'Sin nombre';
                        
                        return (
                          <tr key={reserva.id}>
                            <td>{formatearFecha(reserva.fechaEntrada || reserva.checkIn)}</td>
                            <td>{formatearFecha(reserva.fechaSalida || reserva.checkOut)}</td>
                            <td>{nombre}</td>
                            <td>{obtenerNombreHabitacion(habitacionId)}</td>
                            <td>{personasEnHabitacion}</td>
                            <td>{duracion} {duracion === 1 ? 'noche' : 'noches'}</td>
                            <td>{formatearMoneda(precioTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'right', fontWeight: 'bold' }}>
                          Total {reservasMes.length} {reservasMes.length === 1 ? 'reserva' : 'reservas'}:
                        </td>
                        <td>{formatearMoneda(ingresosMensuales[mesActual] || 0)}</td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="mensaje-sin-reservas">
                    No hay reservas registradas para {meses[mesActual]} {anioActual}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Ingresos por año */}
          <div className={`card ingresos-anuales ${tarjetasColapsadas.ingresosAnuales ? 'colapsado' : ''}`}>
            <div className="card-header">
              <h2>Ingresos Anuales</h2>
              <button 
                className="toggle-btn" 
                onClick={() => toggleTarjeta('ingresosAnuales')}
                aria-label={tarjetasColapsadas.ingresosAnuales ? 'Expandir' : 'Colapsar'}
              >
                {tarjetasColapsadas.ingresosAnuales ? '+' : '−'}
              </button>
            </div>
            <div className="card-content">
              <div className="tabla-container">
                <table>
                  <thead>
                    <tr>
                      <th>Año</th>
                      <th>Ingreso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(ingresosAnuales).sort((a, b) => parseInt(b) - parseInt(a)).map(anio => (
                      <tr key={anio} className={parseInt(anio) === anioActual ? 'anio-actual' : ''}>
                        <td>{anio}</td>
                        <td>{formatearMoneda(ingresosAnuales[anio])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Ingresos por habitación */}
          <div className={`card ingresos-habitaciones ${tarjetasColapsadas.ingresosHabitaciones ? 'colapsado' : ''}`}>
            <div className="card-header">
              <h2>Ingresos por Habitación</h2>
              <button 
                className="toggle-btn" 
                onClick={() => toggleTarjeta('ingresosHabitaciones')}
                aria-label={tarjetasColapsadas.ingresosHabitaciones ? 'Expandir' : 'Colapsar'}
              >
                {tarjetasColapsadas.ingresosHabitaciones ? '+' : '−'}
              </button>
            </div>
            <div className="card-content">
              <div className="tabla-container">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Habitación</th>
                      <th>Reservas</th>
                      <th>Mensual ({meses[mesActual]})</th>
                      <th>Anual ({anioActual})</th>
                      <th>Total Histórico</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(ingresosPorHabitacion).map(id => (
                      <tr key={id}>
                        <td>{id}</td>
                        <td>{ingresosPorHabitacion[id].nombre}</td>
                        <td>{ingresosPorHabitacion[id].numeroReservas}</td>
                        <td>{formatearMoneda(ingresosPorHabitacion[id].ingresoMensual)}</td>
                        <td>{formatearMoneda(ingresosPorHabitacion[id].ingresoAnual)}</td>
                        <td>{formatearMoneda(ingresosPorHabitacion[id].ingresoTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Componente de menú flotante */}
      <FloatingMenu />
    </div>
  );
};

export default AdminFinanzas; 